import { useCallback, useEffect, useState } from 'react';

import { createStorageEventsHandler } from './events';

type StorageUtilsHooks = Readonly<{
	useStorageCheck: () => void;
	useStorageValueBase: (key: string) => [string | null, (value: string | null) => void];
	useStorageValueAsyncBase: (key: string) => [string | null | undefined, (value: string | null) => void];
	useStorageValueSetter: (key: string) => (value: string | null) => void;
	useListener: (key: string, setValue: (value: string | null) => void, options?: { skip?: boolean }) => void;
}>;

export function createStorageUtilsHooks(storage: Storage | undefined | null): StorageUtilsHooks {
	const eventsHandler = createStorageEventsHandler(storage || undefined);

	/** A storage that throws on read must not reach the error boundary. Writes still throw, so callers can report a full store. */
	const read = (key: string): string | null => {
		try {
			return storage ? storage.getItem(key) : null;
		} catch {
			return null;
		}
	};

	return {
		useStorageCheck: () => {
			if (storage === undefined)
				throw new Error('Storage not available in SSR env - use xxxAsync version of hook instead');
		},
		useStorageValueBase: (key: string) => {
			const [value, setValue] = useState(() => read(key));
			const setNullValue = useCallback(() => setValue(null), []);
			return [value, storage ? setValue : setNullValue];
		},
		useStorageValueAsyncBase: (key: string) => {
			// Keyed, so a value read for one key is never served for another. After a key change the value
			// is `undefined` until the listener re-reads it, one render later.
			const [state, setState] = useState<{ key: string; value: string | null | undefined }>({ key, value: undefined });
			const setValue = useCallback((value: string | null) => setState({ key, value: storage ? value : null }), [key]);
			return [state.key === key ? state.value : undefined, setValue];
		},
		useStorageValueSetter: (key: string) =>
			useCallback(
				(val: string | null) => {
					if (!storage) return;
					if (val === null) {
						storage.removeItem(key);
					} else {
						storage.setItem(key, val);
					}
					eventsHandler.trigger(key);
				},
				[key],
			),
		useListener: (key: string, setValue: (value: string | null) => void, { skip = false } = {}) => {
			useEffect(() => {
				if (skip) return undefined;
				setValue(read(key));
				const listener = () => setValue(read(key));
				return eventsHandler.subscribe(key, listener);
			}, [key, setValue, skip]);
		},
	};
}
